import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BRAND, MISSION_ITEMS } from "@/data/content";
import { Sparkles, ShieldCheck, Heart, Link as LinkIcon, ShoppingBag, TrendingUp, Youtube, BookOpen } from "lucide-react";

const PORTRAIT = "https://images.pexels.com/photos/33568641/pexels-photo-33568641.jpeg";

const ICONS = { sparkles: Sparkles, link: LinkIcon, shopping: ShoppingBag, trending: TrendingUp, youtube: Youtube, book: BookOpen };

export default function About() {
    return (
        <>
            <PageHeader
                eyebrow="About"
                title="Welcome to AllAboutYouBuy."
                subtitle="Our mission is to help you buy smarter — hand-tested product picks, honest reviews and beginner-friendly buying guides for every budget."
            />

            <section className="container-editorial pb-16 grid md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-5">
                    <div className="aspect-[3/4] overflow-hidden rounded-lg border border-stone-200 relative">
                        <img
                            src={PORTRAIT}
                            alt="Founder — AllAboutYouBuy"
                            data-testid="about-founder-photo"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-stone-200 rounded-md p-3">
                            <p className="text-[10px] tracking-widest uppercase text-stone-500">Founder</p>
                            <p className="font-serif text-lg leading-tight mt-0.5">{BRAND.founder}</p>
                        </div>
                    </div>
                </div>
                <div className="md:col-span-7 space-y-5 text-stone-700 text-lg leading-relaxed">
                    <p>
                        We publish honest reviews, practical buying guides and free resources — so you can find the right
                        product, at the right price, the first time.
                    </p>
                    <p>
                        Whether you're hunting for a gadget, a kitchen upgrade or the next tool to make life easier,
                        AllAboutYouBuy is here to make the decision simple and stress-free.
                    </p>
                    <p className="font-serif text-2xl md:text-3xl text-primary italic pt-2">
                        Find it. Love it. Buy it.
                    </p>
                    <div className="pt-4 flex flex-wrap gap-3">
                        <Button asChild data-testid="about-cta-ebook" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 text-primary-foreground">
                            <Link to="/ebooks">Grab a free eBook</Link>
                        </Button>
                        <Button asChild variant="outline" className="rounded-full h-12 px-7 border-stone-300">
                            <Link to="/contact">Say hello</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Mission strip */}
            <section data-testid="about-mission" className="bg-stone-100 border-y border-stone-200">
                <div className="container-editorial py-20 md:py-24">
                    <div className="max-w-2xl">
                        <p className="label-eyebrow text-primary">Website Mission</p>
                        <h2 className="font-serif text-3xl md:text-5xl leading-tight mt-3">
                            Our goal is to become a trusted resource for:
                        </h2>
                    </div>
                    <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {MISSION_ITEMS.map((m) => {
                            const Icon = ICONS[m.icon] || Sparkles;
                            return (
                                <li key={m.label} data-testid={`mission-${m.icon}`} className="bg-white border border-stone-200 rounded-lg p-5 flex items-center gap-4">
                                    <span className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <span className="font-serif text-xl leading-tight">{m.label}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>

            {/* Values */}
            <section className="container-editorial py-20 md:py-24">
                <div className="grid sm:grid-cols-3 gap-8">
                    <div>
                        <Sparkles className="h-6 w-6 text-primary" />
                        <p className="font-serif text-xl mt-3">Curated, not scraped</p>
                        <p className="text-sm text-stone-500 mt-2">Every recommendation is hand-tested first.</p>
                    </div>
                    <div>
                        <ShieldCheck className="h-6 w-6 text-primary" />
                        <p className="font-serif text-xl mt-3">Radical honesty</p>
                        <p className="text-sm text-stone-500 mt-2">If something bombs for us, we say so.</p>
                    </div>
                    <div>
                        <Heart className="h-6 w-6 text-primary" />
                        <p className="font-serif text-xl mt-3">Beginner-first</p>
                        <p className="text-sm text-stone-500 mt-2">Zero jargon, zero gatekeeping.</p>
                    </div>
                </div>
            </section>

            {/* Affiliate note */}
            <section className="container-editorial pb-24">
                <div className="bg-white border border-stone-200 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start gap-6">
                    <ShieldCheck className="h-8 w-8 text-primary shrink-0" />
                    <div className="flex-1">
                        <h3 className="font-serif text-2xl">Partners & Referral Disclosure</h3>
                        <p className="text-stone-600 mt-2 leading-relaxed">
                            Some links on this website are partner links. This means we may earn a small referral reward if you
                            purchase through our links, at no extra cost to you. We only recommend products and services that
                            we believe provide value to our readers.{" "}
                            <Link to="/affiliate-disclosure" className="underline text-primary">Read the full disclosure</Link>.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
